import { get, isUndefined } from 'lodash-es';

import {
  IFindEntryOptions,
  IInRestaurantRedemptionCart,
  IInRestaurantRedemptionCartEntry,
  IInRestaurantRedemptionEntryType,
  IInRestaurantRedemptionOfferEntry,
  IInRestaurantRedemptionRewardEntry,
  InRestaurantIncentives,
  isEngineReward,
  isLegacyOffer,
  isLoyaltyOffer,
  isLoyaltyReward,
} from './types';
import { isQuantityValid, removeElementAtIndexFromList } from './utils';

export interface ITryCartAction {
  isActionSuccess: boolean;
  cartResult: IInRestaurantRedemptionCart;
}

const findCartEntryIndex = (
  cart: IInRestaurantRedemptionCart,
  { incentiveId: entryIncentiveId }: IInRestaurantRedemptionCartEntry
) => cart.findIndex(({ incentiveId }) => incentiveId === entryIncentiveId);

export const getFirstOccurrenceInCart = (
  cart: IInRestaurantRedemptionCart,
  options?: IFindEntryOptions
): IInRestaurantRedemptionCartEntry | undefined => {
  const idx = cart.findIndex(entry => {
    let match = false;
    if (!isUndefined(options?.type)) {
      match = options?.type === entry.type;
    }

    if (match && options?.keyPath?.length) {
      let currentValue = get(entry, options.keyPath);

      match = currentValue === options.value;
    }

    return match;
  });

  return cart[idx];
};

export const excludeEntryType = (
  cart: IInRestaurantRedemptionCart,
  typeToFilter: IInRestaurantRedemptionEntryType
) => cart.filter(({ type }) => type !== typeToFilter);

/**
 * Adds the given cart entry and replaces an existing item with the same type.
 */
export const addOrReplaceIfExist = (
  cart: IInRestaurantRedemptionCart,
  cartEntry: IInRestaurantRedemptionCartEntry,
  options?: IFindEntryOptions
): ITryCartAction => {
  const existingCartEntry = getFirstOccurrenceInCart(cart, {
    type: cartEntry.type,
    ...options,
  });
  let cartResult = cart;

  if (existingCartEntry) {
    cartResult = removeCartEntry(cart, existingCartEntry);
  }

  return addToInRestaurantCart(cartResult, cartEntry);
};

/**
 * Adds entry to the cart.
 */
export const addToInRestaurantCart = (
  cart: IInRestaurantRedemptionCart,
  cartEntry: IInRestaurantRedemptionCartEntry
): ITryCartAction => ({
  isActionSuccess: true,
  cartResult: cart.concat(cartEntry),
});

/**
 * Adds or updates the item quantity by one if it already exists in the cart.
 */
export const addOrUpdateQuantityIfExist = (
  cart: IInRestaurantRedemptionCart,
  cartEntry: IInRestaurantRedemptionCartEntry
): ITryCartAction => {
  let result: ITryCartAction = {
    isActionSuccess: true,
    cartResult: cart,
  };
  const existingRewardIndex = findCartEntryIndex(cart, cartEntry);

  if (existingRewardIndex < 0) {
    result = addToInRestaurantCart(cart, cartEntry);
  } else {
    // update the quantity if already exists
    const existedEntry = cart[existingRewardIndex];
    const quantity = existedEntry.quantity + 1;

    result = updateCartEntryQuantity(cart, {
      cartEntry,
      quantity,
    });
  }

  return result;
};

/**
 * Returns the cart entry associated to the given entity or `undefined` if is not found.
 */
export const findCartElementByIncentive = (
  cart: IInRestaurantRedemptionCart,
  entity: InRestaurantIncentives
): IInRestaurantRedemptionCartEntry | undefined => {
  let entityType = IInRestaurantRedemptionEntryType.REWARD;
  let checkIdentifier: ((cartEntry: IInRestaurantRedemptionCartEntry) => boolean) | undefined;

  if (isLoyaltyReward(entity)) {
    checkIdentifier = (cartEntry: IInRestaurantRedemptionRewardEntry) =>
      cartEntry.details.reward._id === entity._id;
  } else if (isLoyaltyOffer(entity)) {
    entityType = IInRestaurantRedemptionEntryType.OFFER;
    checkIdentifier = (cartEntry: IInRestaurantRedemptionOfferEntry) =>
      cartEntry.details.offer._id === entity._id;
  } else if (isLegacyOffer(entity)) {
    entityType = IInRestaurantRedemptionEntryType.LEGACY_OFFER;
    checkIdentifier = (cartEntry: IInRestaurantRedemptionOfferEntry) =>
      cartEntry.details.offer._id === entity._id;
  } else if (isEngineReward(entity)) {
    checkIdentifier = (cartEntry: IInRestaurantRedemptionRewardEntry) =>
      cartEntry.details.engineReward.id === entity.id;
  }

  return checkIdentifier
    ? cart.find(cartEntry => cartEntry.type === entityType && checkIdentifier?.(cartEntry))
    : undefined;
};

/**
 * Removes the given cart entry from the cart.
 */
export const removeCartEntry = (
  cart: IInRestaurantRedemptionCart,
  cartEntry: IInRestaurantRedemptionCartEntry
): IInRestaurantRedemptionCart => {
  const entryIndex = findCartEntryIndex(cart, cartEntry);

  return removeElementAtIndexFromList(cart, entryIndex);
};

/**
 * Updates the entry quantity if the quantity is correct and the item exists in the cart.
 */
export const updateCartEntryQuantity = (
  cart: IInRestaurantRedemptionCart,
  { cartEntry, quantity }: { cartEntry: IInRestaurantRedemptionCartEntry; quantity: number }
): ITryCartAction => {
  const entryIndex = findCartEntryIndex(cart, cartEntry);

  if (entryIndex < 0 || !isQuantityValid(quantity)) {
    return { isActionSuccess: false, cartResult: cart };
  }

  const cartResult = removeElementAtIndexFromList(cart, entryIndex);
  const prevCartEntry = cart[entryIndex];

  cartResult.splice(entryIndex, 0, {
    ...prevCartEntry,
    quantity,
  });

  return { isActionSuccess: true, cartResult };
};
